import { useEffect, useRef, useState } from 'react';
import { buildImageAlt } from '../../utils/imageMedia';

const LIGHTBOX_ZOOM_SCALE = 2;

const ImageLightbox = ({
    imageTitle,
    imageUrl,
    onClose
}) => {
    const [isZoomed, setIsZoomed] = useState(false);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const dragStateRef = useRef({
        active: false,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0,
        moved: false
    });

    useEffect(() => {
        if (isZoomed) {
            return;
        }

        setPanOffset({ x: 0, y: 0 });
    }, [isZoomed]);

    const toggleZoom = () => {
        setIsZoomed((prev) => !prev);
    };

    const handlePointerDown = (event) => {
        if (!isZoomed) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        dragStateRef.current = {
            active: true,
            startX: event.clientX,
            startY: event.clientY,
            originX: panOffset.x,
            originY: panOffset.y,
            moved: false
        };
    };

    const handlePointerMove = (event) => {
        if (!dragStateRef.current.active || !isZoomed) {
            return;
        }

        event.preventDefault();

        const deltaX = event.clientX - dragStateRef.current.startX;
        const deltaY = event.clientY - dragStateRef.current.startY;

        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
            dragStateRef.current.moved = true;
        }

        setPanOffset({
            x: dragStateRef.current.originX + deltaX,
            y: dragStateRef.current.originY + deltaY
        });
    };

    const stopDragging = () => {
        dragStateRef.current.active = false;
    };

    return (
        <div
            className="image-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`Full screen view of ${imageTitle}`}
            onClick={onClose}
        >
            <div className="image-lightbox-controls">
                <button
                    type="button"
                    className="image-lightbox-action"
                    aria-label={isZoomed ? 'Reset zoom' : 'Zoom image'}
                    onClick={(event) => {
                        event.stopPropagation();
                        toggleZoom();
                    }}
                >
                    {isZoomed ? 'Reset zoom' : 'Zoom 2x'}
                </button>

                <button
                    type="button"
                    className="image-lightbox-close"
                    aria-label="Close full screen image"
                    onClick={onClose}
                >
                    Close
                </button>
            </div>

            <div
                className={`image-lightbox-stage ${isZoomed ? 'is-zoomed' : ''}`}
                onClick={(event) => event.stopPropagation()}
                onPointerMove={handlePointerMove}
                onPointerUp={stopDragging}
                onPointerLeave={stopDragging}
            >
                <img
                    src={imageUrl}
                    alt={buildImageAlt(imageTitle)}
                    className={isZoomed ? 'is-zoomed' : ''}
                    style={{
                        transform: isZoomed
                            ? `translate(${panOffset.x}px, ${panOffset.y}px) scale(${LIGHTBOX_ZOOM_SCALE})`
                            : 'translate(0px, 0px) scale(1)'
                    }}
                    onClick={() => {
                        if (dragStateRef.current.moved) {
                            dragStateRef.current.moved = false;
                            return;
                        }

                        toggleZoom();
                    }}
                    onPointerDown={handlePointerDown}
                />
            </div>
        </div>
    );
};

export default ImageLightbox;
